import {
  type Dashboard,
  DASHBOARD_RANGE_LABELS,
  type DashboardRange,
  LOT_STATUS_LABELS,
} from '@edms/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  CopyCheck,
  HardDrive,
  Layers,
  ScanLine,
  ShieldCheck,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { PageBody, PageHeader } from '@/components/page-header';
import { Select } from '@/components/ui/primitives';
import {
  Badge,
  Card,
  EmptyState,
  ProgressBar,
  SectionHeader,
  Skeleton,
  StatCard,
} from '@/components/ui/surfaces';
import { api } from '@/lib/api-client';
import { cn, formatBytes, formatDate, formatMonth, formatNumber, formatPercent, formatRelative } from '@/lib/utils';

/**
 * Tableau de bord (§12).
 *
 * Trois strates de lecture, dans cet ordre : ce qui demande une action
 * aujourd'hui (les cartes), la tendance (les graphiques), puis le détail par
 * bureau. Un responsable doit pouvoir décider en regardant la première ligne.
 */
export function DashboardPage(): React.JSX.Element {
  const [range, setRange] = useState<DashboardRange>('MONTH');
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard', range],
    queryFn: () => api.get<Dashboard>('/dashboard', { range }),
    refetchInterval: 60_000,
  });

  return (
    <>
      <PageHeader
        title="Tableau de bord"
        description="Activité d'archivage, exceptions en cours et occupation du stockage"
        actions={
          <Select
            size="sm"
            value={range}
            onChange={(event) => setRange(event.target.value as DashboardRange)}
            aria-label="Période analysée"
            className="w-44"
          >
            {(Object.keys(DASHBOARD_RANGE_LABELS) as DashboardRange[]).map((value) => (
              <option key={value} value={value}>
                {DASHBOARD_RANGE_LABELS[value]}
              </option>
            ))}
          </Select>
        }
      />

      <PageBody>
        {isError ? (
          <Card>
            <EmptyState
              icon={AlertTriangle}
              title="Indicateurs indisponibles"
              description="Les statistiques n'ont pas pu être calculées. Réessayez dans un instant."
            />
          </Card>
        ) : isLoading || !data ? (
          <DashboardSkeleton />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 xl:grid-cols-3 2xl:grid-cols-6">
              <StatCard
                label="Reçus archivés"
                value={formatNumber(data.summary.archivedReceipts.value)}
                icon={Archive}
                tone="success"
                changeRatio={data.summary.archivedReceipts.changeRatio}
                hint={DASHBOARD_RANGE_LABELS[data.range].toLowerCase()}
              />
              <StatCard
                label="Lots créés"
                value={formatNumber(data.summary.lotsToday.value)}
                icon={Layers}
                tone="accent"
                changeRatio={data.summary.lotsToday.changeRatio}
              />
              <StatCard
                label="OCR en attente"
                value={formatNumber(data.summary.ocrPending.value)}
                icon={ScanLine}
                tone={data.summary.ocrPending.value > 0 ? 'info' : 'neutral'}
                hint="documents en file"
              />
              <StatCard
                label="Validation requise"
                value={formatNumber(data.summary.pendingReview.value)}
                icon={ShieldCheck}
                tone={data.summary.pendingReview.value > 0 ? 'warning' : 'neutral'}
                hint="exceptions ouvertes"
              />
              <StatCard
                label="Doublons détectés"
                value={formatNumber(data.summary.duplicatesDetected.value)}
                icon={CopyCheck}
                tone={data.summary.duplicatesDetected.value > 0 ? 'warning' : 'neutral'}
                changeRatio={data.summary.duplicatesDetected.changeRatio}
                invertChange
              />
              <StatCard
                label="Erreurs"
                value={formatNumber(data.summary.errors.value)}
                icon={AlertTriangle}
                tone={data.summary.errors.value > 0 ? 'danger' : 'neutral'}
                changeRatio={data.summary.errors.changeRatio}
                invertChange
              />
            </div>

            <div className="grid gap-4 xl:grid-cols-3">
              <Card className="xl:col-span-2">
                <SectionHeader
                  title="Progression quotidienne"
                  description="Documents numérisés et archivés, jour par jour"
                />
                <div className="mt-5 h-64">
                  <ActivityChart series={data.series} />
                </div>
              </Card>

              <div className="space-y-4">
                <Card>
                  <SectionHeader title="Qualité de la reconnaissance" />
                  <dl className="mt-4 space-y-3.5">
                    <MetricRow
                      label="Confiance OCR moyenne"
                      value={formatPercent(data.summary.averageOcrConfidence, 1)}
                    />
                    <MetricRow
                      label="Durée moyenne par document"
                      value={
                        data.summary.averageProcessingMs === null
                          ? '—'
                          : `${formatNumber(Math.round(data.summary.averageProcessingMs))} ms`
                      }
                    />
                    <MetricRow
                      label="Rythme du jour"
                      value={`${data.summary.dailyProgressRatio.toFixed(2).replace('.', ',')} ×`}
                      hint="par rapport à la moyenne de la période"
                    />
                  </dl>
                </Card>

                <StorageCard storage={data.storage} />
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-3">
              <Card className="xl:col-span-2">
                <SectionHeader
                  title="Activité des bureaux"
                  description="Cliquez sur un bureau pour ouvrir ses lots"
                />
                <div className="mt-4 -mx-5 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-y border-line-subtle">
                        <th className="px-5 py-2 text-left text-2xs font-semibold tracking-wide text-ink-faint uppercase">
                          Bureau
                        </th>
                        <th className="px-3 py-2 text-right text-2xs font-semibold tracking-wide text-ink-faint uppercase">
                          Archivés
                        </th>
                        <th className="px-3 py-2 text-right text-2xs font-semibold tracking-wide text-ink-faint uppercase">
                          À valider
                        </th>
                        <th className="px-3 py-2 text-right text-2xs font-semibold tracking-wide text-ink-faint uppercase">
                          Doublons
                        </th>
                        <th className="px-3 py-2 text-right text-2xs font-semibold tracking-wide text-ink-faint uppercase">
                          Lots actifs
                        </th>
                        <th className="px-5 py-2 text-right text-2xs font-semibold tracking-wide text-ink-faint uppercase">
                          Dernière activité
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line-subtle">
                      {data.branches.map((branch) => (
                        <tr
                          key={branch.branchId}
                          onClick={() => navigate(`/numerisation?branchId=${branch.branchId}`)}
                          className="cursor-pointer transition-colors hover:bg-hover"
                        >
                          <td className="px-5 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-2xs text-ink-faint">{branch.branchCode}</span>
                              <span className="truncate text-ink">{branch.branchName}</span>
                            </div>
                          </td>
                          <td className="tabular px-3 py-2.5 text-right text-ink">
                            {formatNumber(branch.archived)}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {branch.pendingReview > 0 ? (
                              <Badge tone="warning">{branch.pendingReview}</Badge>
                            ) : (
                              <span className="text-ink-faint">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {branch.duplicates > 0 ? (
                              <Badge tone="warning">{branch.duplicates}</Badge>
                            ) : (
                              <span className="text-ink-faint">—</span>
                            )}
                          </td>
                          <td className="tabular px-3 py-2.5 text-right text-ink-muted">
                            {branch.activeLots || '—'}
                          </td>
                          <td className="px-5 py-2.5 text-right text-xs text-ink-faint">
                            {branch.lastActivityAt ? formatRelative(branch.lastActivityAt) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card>
                <SectionHeader title="Évolution mensuelle" description="12 derniers mois" />
                <div className="mt-5 h-52">
                  <MonthlyChart trend={data.monthlyTrend} />
                </div>

                <div className="mt-5 border-t border-line-subtle pt-4">
                  <p className="mb-2.5 text-2xs font-semibold tracking-wide text-ink-faint uppercase">
                    Répartition des lots
                  </p>
                  <ul className="space-y-1.5">
                    {data.lotStatuses.map((entry) => (
                      <li key={entry.status} className="flex items-center justify-between text-xs">
                        <span className="text-ink-muted">{LOT_STATUS_LABELS[entry.status]}</span>
                        <span className="tabular font-medium text-ink">{formatNumber(entry.count)}</span>
                      </li>
                    ))}
                    {data.lotStatuses.length === 0 && (
                      <li className="text-xs text-ink-faint">Aucun lot enregistré.</li>
                    )}
                  </ul>
                </div>
              </Card>
            </div>
          </>
        )}
      </PageBody>
    </>
  );
}

function MetricRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-ink-muted">
        {label}
        {hint && <span className="mt-0.5 block text-2xs text-ink-faint">{hint}</span>}
      </dt>
      <dd className="tabular text-sm font-semibold text-ink">{value}</dd>
    </div>
  );
}

function StorageCard({ storage }: { storage: Dashboard['storage'] }): React.JSX.Element {
  const usedRatio = storage.totalBytes > 0 ? (storage.usedBytes / storage.totalBytes) * 100 : 0;

  return (
    <Card>
      <SectionHeader
        title="Stockage"
        actions={
          storage.isLow ? (
            <Badge tone="danger" dot>
              Espace faible
            </Badge>
          ) : undefined
        }
      />

      <div className="mt-4 space-y-3">
        <div>
          <div className="mb-1.5 flex items-baseline justify-between text-xs">
            <span className="text-ink-muted">Volume du serveur</span>
            <span className="tabular text-ink-faint">
              {storage.totalBytes > 0
                ? `${formatBytes(storage.freeBytes)} libres`
                : 'non mesurable'}
            </span>
          </div>
          <ProgressBar
            value={usedRatio}
            tone={storage.isLow ? 'danger' : usedRatio > 80 ? 'warning' : 'accent'}
          />
        </div>

        <dl className="grid grid-cols-2 gap-3 border-t border-line-subtle pt-3">
          <div>
            <dt className="text-2xs text-ink-faint">Documents archivés</dt>
            <dd className="tabular mt-0.5 text-sm font-semibold text-ink">
              {formatNumber(storage.documentCount)}
            </dd>
          </div>
          <div>
            <dt className="text-2xs text-ink-faint">Volume occupé</dt>
            <dd className="tabular mt-0.5 flex items-center gap-1.5 text-sm font-semibold text-ink">
              <HardDrive className="size-3.5 text-ink-faint" aria-hidden />
              {formatBytes(storage.documentBytes)}
            </dd>
          </div>
        </dl>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------------ */
/* Graphiques                                                                */
/* ------------------------------------------------------------------------ */

const AXIS_STYLE = { fontSize: 11, fill: 'hsl(var(--text-tertiary))' } as const;

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: readonly { name?: string; value?: number; color?: string }[];
  label?: string;
}): React.JSX.Element | null {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-md border border-line bg-raised px-3 py-2 shadow-[var(--shadow-raised)]">
      <p className="mb-1.5 text-2xs font-medium text-ink-faint">{label}</p>
      <ul className="space-y-0.5">
        {payload.map((entry, index) => (
          <li key={index} className="flex items-center gap-2 text-xs">
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: entry.color }}
              aria-hidden
            />
            <span className="text-ink-muted">{entry.name}</span>
            <span className="tabular ml-auto font-medium text-ink">{formatNumber(entry.value ?? 0)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActivityChart({ series }: { series: Dashboard['series'] }): React.JSX.Element {
  if (series.every((point) => point.archived === 0 && point.scanned === 0)) {
    return (
      <EmptyState
        title="Aucune activité sur la période"
        description="Les documents numérisés et archivés apparaîtront ici."
      />
    );
  }

  const data = series.map((point) => ({ ...point, label: formatDate(point.date) }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -18 }}>
        <defs>
          <linearGradient id="archived-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.28} />
            <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="hsl(var(--border-subtle))" vertical={false} />
        <XAxis
          dataKey="label"
          tick={AXIS_STYLE}
          tickLine={false}
          axisLine={false}
          minTickGap={28}
        />
        <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} width={44} />
        <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'hsl(var(--border-strong))' }} />
        <Area
          type="monotone"
          dataKey="scanned"
          name="Numérisés"
          stroke="hsl(var(--info))"
          strokeWidth={1.5}
          fill="transparent"
          strokeDasharray="4 3"
          dot={false}
        />
        <Area
          type="monotone"
          dataKey="archived"
          name="Archivés"
          stroke="hsl(var(--accent))"
          strokeWidth={2}
          fill="url(#archived-gradient)"
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function MonthlyChart({ trend }: { trend: Dashboard['monthlyTrend'] }): React.JSX.Element {
  if (trend.length === 0) {
    return <EmptyState title="Pas encore d'historique" />;
  }

  const data = trend.map((point) => ({ ...point, label: formatMonth(point.month) }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -22 }}>
        <CartesianGrid stroke="hsl(var(--border-subtle))" vertical={false} />
        <XAxis dataKey="label" tick={AXIS_STYLE} tickLine={false} axisLine={false} minTickGap={12} />
        <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} width={44} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'hsl(var(--surface-hover))' }} />
        <Bar dataKey="archived" name="Archivés" fill="hsl(var(--accent))" radius={[3, 3, 0, 0]} maxBarSize={26} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function DashboardSkeleton(): React.JSX.Element {
  return (
    <>
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-3 2xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, index) => (
          <Card key={index} className="space-y-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-7 w-16" />
          </Card>
        ))}
      </div>
      <div className={cn('grid gap-4 xl:grid-cols-3')}>
        <Card className="xl:col-span-2">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="mt-5 h-64 w-full" />
        </Card>
        <Card>
          <Skeleton className="h-3 w-32" />
          <Skeleton className="mt-5 h-52 w-full" />
        </Card>
      </div>
    </>
  );
}
